import React from "react"
import moment from "moment"
import styles from "./modalOutboundCampaign.module.css"
import Button from "../../Form/Button"
import FormGroupInline from "../../Form/FormGroupInline"
import BreakForm from "../../Form/BreakForm"
import { useData } from "../../../contexts/dataContext"
import { normalizeCampaignPhone } from "./ConfigContacts"

const formatMoney = (value = 0) => Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
const formatUsd = (value = 0) => Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 4, maximumFractionDigits: 4 })

const ConfigConfirmation = ({ data, dataFile, selectedTemplate, inboxes = [], message = {}, costEstimate = null, isLoadingCostEstimate = false, canUpdateCampaignLimits = false, onOpenLimitSettings = () => { }, handleChangeStep = () => { }, onSubmit = () => { } }) => {
    const { teams = [], roles = [], users = [] } = useData()
    const inbox = inboxes.find(inbox => inbox.id === data?.inbox_id)
    const phones = (dataFile?.rows || []).map(row => normalizeCampaignPhone(row?.[data?.contact_phone])).filter(phone => phone.length >= 10 && phone.length <= 15)
    const uniqueContacts = new Set(phones).size
    const duplicateContacts = phones.length - uniqueContacts
    const selectedUserIds = new Set((data?.assignment_users || []).map(String))
    const selectedUserNames = users.filter(user => selectedUserIds.has(String(user.id))).map(user => user.value).join(", ")
    const roundRobinUserIds = new Set((data?.assignment_round_robin?.users || []).map(String))
    const roundRobinUserNames = users.filter(user => roundRobinUserIds.has(String(user.id))).map(user => user.value).join(", ")
    const getAssignmentLabel = () => {
        if (data?.assignment_mode === "users" && data?.assignment_users?.includes("auto")) {
            const roundRobinLabel = data?.assignment_round_robin?.mode === "custom"
                ? `Rodízio entre: ${roundRobinUserNames || "usuários selecionados"}`
                : "Rodízio pelos cargos habilitados"
            return selectedUserNames ? `${roundRobinLabel}; também: ${selectedUserNames}` : roundRobinLabel
        }
        if (data?.assignment_mode === "users") return selectedUserNames || "Usuários selecionados"
        if (data?.assignment_mode === "ai_agent") return data?.assignment_agent_name || "Agente de IA selecionado"
        if (data?.assignment_mode === "teamAndRole") {
            const team = teams.find(item => item.id === data?.assignment_team_id)?.value
            const role = roles.find(item => item.id === data?.assignment_role_id)?.value
            return [team, role].filter(Boolean).join(" / ") || "Equipe/cargo selecionado"
        }
        return "Regra padrão da caixa"
    }
    const sendPolicy = costEstimate?.send_policy
    const capacity = sendPolicy?.capacity || {}
    const warmup = capacity?.warmup || {}
    const warmupSchedule = warmup?.schedule || []
    const dateScheduleSend = moment.isMoment(data?.date_schedule_send)
        ? data.date_schedule_send.format("YYYY-MM-DD")
        : data?.date_schedule_send
    const scheduledAt = data?.when_send === "SCHEDULE" && data?.date_schedule_send && data?.time_schedule_send
        ? moment(`${dateScheduleSend} ${data.time_schedule_send}`, "YYYY-MM-DD HH:mm").format("DD/MM/YYYY [às] HH:mm")
        : "Enviar agora"

    return (
        <>
            <BreakForm title="Confirmar campanha" border={false} showAction={false} />
            <div className={styles.confirmationGrid}>
                <div className={styles.confirmationItem}>
                    <span>Campanha</span>
                    <strong>{data?.name || "-"}</strong>
                </div>
                <div className={styles.confirmationItem}>
                    <span>Caixa de entrada</span>
                    <strong>{inbox?.value || "-"}</strong>
                </div>
                <div className={styles.confirmationItem}>
                    <span>Envios únicos</span>
                    <strong>{uniqueContacts}</strong>
                </div>
                <div className={styles.confirmationItem}>
                    <span>Envio</span>
                    <strong>{scheduledAt}</strong>
                </div>
                <div className={styles.confirmationItem}>
                    <span>Template</span>
                    <strong>{selectedTemplate?.name || message?.content?.template_name || "-"}</strong>
                </div>
                <div className={styles.confirmationItem}>
                    <span>Categoria</span>
                    <strong>{selectedTemplate?.category || message?.category || "-"}</strong>
                </div>
                <div className={styles.confirmationItem}>
                    <span>Lista de conversas</span>
                    <strong>{data?.update_conversations === false ? "Não mover conversas pelo disparo" : "Mostrar disparo como última mensagem"}</strong>
                </div>
                <div className={styles.confirmationItem}>
                    <span>Responsável</span>
                    <strong>{getAssignmentLabel()}</strong>
                </div>
                {data?.assignment_mode !== "inbox_default" && <div className={styles.confirmationItem}>
                    <span>Conversas existentes</span>
                    <strong>{data?.existing_conversations === "replace" ? "Substituir responsável" : "Preservar quando já atribuído"}</strong>
                </div>}
                {duplicateContacts > 0 && <div className={styles.confirmationItem}>
                    <span>Duplicados ignorados</span>
                    <strong>{duplicateContacts}</strong>
                </div>}
            </div>
            <div className={`${styles.costEstimateBox} ${costEstimate?.exceeded ? styles.blocked : ""}`}>
                <div>
                    <span>Custo estimado do disparo</span>
                    <strong>{isLoadingCostEstimate ? "Calculando..." : formatMoney(costEstimate?.estimate?.cost_brl)}</strong>
                    <small>Considerando que todas as mensagens sejam entregues. Taxa: {formatUsd(costEstimate?.estimate?.rate_usd)} por mensagem, convertida com US$ 1 = {formatMoney(costEstimate?.estimate?.exchange_rate?.rate)}.</small>
                </div>
                <div>
                    <span>Uso mensal projetado</span>
                    <strong>{formatMoney(costEstimate?.projected_cost_brl)}</strong>
                    <small>
                        {costEstimate?.limit?.enabled
                            ? `Limite ${costEstimate.limit.scope === "inbox" ? "da caixa" : "geral"}: ${formatMoney(costEstimate.limit.amount_brl)}`
                            : "Sem limite mensal configurado."}
                        {costEstimate?.month_reference ? ` Mês de referência: ${costEstimate.month_reference}.` : ""}
                    </small>
                </div>
            </div>
            {costEstimate?.exceeded &&
                <div className={styles.limitBlockedNotice}>
                    Esta campanha ultrapassa o limite mensal configurado. Reduza a lista, ajuste o limite em Limites de campanhas ou fale com o responsável pela conta.
                    {canUpdateCampaignLimits &&
                        <button type="button" onClick={onOpenLimitSettings}>Alterar limite</button>
                    }
                </div>
            }
            {sendPolicy &&
                <div className={`${styles.policyNotice} ${sendPolicy.blocked ? styles.blocked : ""}`}>
                    <div className={styles.policyHeader}>
                        <div>
                            <span>Saúde do número</span>
                            <strong>{sendPolicy.quality_rating || "UNKNOWN"}</strong>
                        </div>
                        <div>
                            <span>Saldo 24h</span>
                            <strong>{capacity.remaining_24h ?? "-"} / {capacity.daily_limit ?? "-"}</strong>
                        </div>
                    </div>
                    {sendPolicy.messages?.length > 0 &&
                        <div className={styles.policyMessages}>
                            {sendPolicy.messages.map((item, index) => (
                                <div key={`${item.title}-${index}`} className={styles.policyMessage}>
                                    <strong>{item.title}</strong>
                                    <span>{item.description}</span>
                                </div>
                            ))}
                        </div>
                    }
                    {warmupSchedule.length > 0 &&
                        <div className={styles.warmupSchedule}>
                            {warmupSchedule.map(item => {
                                const isCurrent = warmup.age_days >= item.min_days && (item.max_days === null || warmup.age_days <= item.max_days)
                                return (
                                    <div key={item.label} className={isCurrent ? styles.current : ""}>
                                        <span>{item.label}</span>
                                        <strong>{item.limit ? `${item.limit} envios/24h` : "Capacidade Meta"}</strong>
                                    </div>
                                )
                            })}
                        </div>
                    }
                </div>
            }
            {data?.update_conversations === false &&
                <div className={styles.confirmationNotice}>
                    A campanha será enviada normalmente e continuará aparecendo no histórico de campanhas. As mensagens do disparo serão registradas na conversa, mas não serão usadas para atualizar a prévia, a data da última mensagem ou a posição da conversa na lista.
                </div>
            }
            {data?.update_conversations !== false &&
                <div className={styles.confirmationNotice}>
                    Cada mensagem enviada pela campanha poderá aparecer como última mensagem da conversa e trazer o contato para o topo da lista de atendimento.
                </div>
            }
            <FormGroupInline>
                <Button variant='secondary' onClick={() => handleChangeStep(4)}>Editar mensagem</Button>
                <Button variant='primary' onClick={() => onSubmit(message)} disabled={costEstimate?.exceeded || sendPolicy?.blocked || isLoadingCostEstimate}>Criar campanha</Button>
            </FormGroupInline>
        </>
    )
}

export default ConfigConfirmation
